import { API_BASE } from '@/shared/services/api';
/**
 * Push Notification Service for PWA
 * Handles Web Push subscription and notification display
 */

const API_URL = API_BASE;

/**
 * Check if push notifications are supported
 */
export const isPushSupported = () => {
  return 'serviceWorker' in navigator && 'PushManager' in window;
};

/**
 * Check if notifications are permitted
 */
export const checkNotificationPermission = () => {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
};

/**
 * Request notification permission
 */
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    return { granted: false, reason: 'Notifications not supported' };
  }

  const permission = await Notification.requestPermission();
  return {
    granted: permission === 'granted',
    permission
  };
};

/**
 * Get VAPID public key from server
 */
export const getVapidKey = async () => {
  try {
    const response = await fetch(`${API_URL}/api/push/vapid-key`);
    if (!response.ok) return null;
    
    const data = await response.json();
    if (!data.configured) return null;
    
    return data.publicKey;
  } catch (error) {
    console.error('[PushService] Failed to get VAPID key:', error);
    return null;
  }
};

/**
 * Convert VAPID key to Uint8Array for subscription
 */
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

/**
 * Subscribe to push notifications
 */
export const subscribeToPush = async () => {
  if (!isPushSupported()) {
    return { success: false, error: 'Push not supported' };
  }

  try {
    // Request permission if needed
    const permission = await requestNotificationPermission();
    if (!permission.granted) {
      return { success: false, error: 'Permission denied' };
    }

    // Get VAPID key
    const vapidKey = await getVapidKey();
    if (!vapidKey) {
      return { success: false, error: 'VAPID key not configured on server' };
    }

    // Get service worker registration
    const registration = await navigator.serviceWorker.ready;

    // Subscribe to push
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey)
    });

    // Send subscription to server
    const token = localStorage.getItem('ezerpToken');
    const userId = (JSON.parse(localStorage.getItem('ezerpUser') || '{}')?.id || '');

    const response = await fetch(`${API_URL}/api/push/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      body: JSON.stringify({
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))),
          auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth'))))
        },
        user_id: userId,
        device_type: detectDeviceType()
      })
    });

    if (!response.ok) {
      throw new Error('Failed to register subscription on server');
    }

    const result = await response.json();
    console.log('[PushService] Subscribed:', result);
    
    return { success: true, subscription, result };

  } catch (error) {
    console.error('[PushService] Subscription failed:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Unsubscribe from push notifications
 */
export const unsubscribeFromPush = async () => {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      // Unsubscribe locally
      await subscription.unsubscribe();

      // Notify server
      const token = localStorage.getItem('ezerpToken');
      await fetch(`${API_URL}/api/push/unsubscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ endpoint: subscription.endpoint })
      });

      return { success: true };
    }

    return { success: true, message: 'No subscription to remove' };

  } catch (error) {
    console.error('[PushService] Unsubscribe failed:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Check if currently subscribed
 */
export const isSubscribed = async () => {
  if (!isPushSupported()) return false;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch (error) {
    return false;
  }
};

/**
 * Detect device type
 */
const detectDeviceType = () => {
  const ua = navigator.userAgent;
  
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  if (/Mobile/.test(ua)) return 'mobile';
  if (window.matchMedia('(display-mode: standalone)').matches) return 'pwa';
  
  return 'desktop';
};

/**
 * Show local notification (for testing or manual triggers)
 */
export const showLocalNotification = (title, options = {}) => {
  if (Notification.permission !== 'granted') {
    console.warn('[PushService] Notification permission not granted');
    return;
  }

  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready.then(registration => {
      registration.showNotification(title, {
        icon: '/icons/notification-icon.png',
        badge: '/icons/badge-icon.png',
        ...options
      });
    });
  } else {
    new Notification(title, options);
  }
};

export default {
  isPushSupported,
  checkNotificationPermission,
  requestNotificationPermission,
  subscribeToPush,
  unsubscribeFromPush,
  isSubscribed,
  showLocalNotification,
  getVapidKey
};
